import {
  useCallback,
  useEffect,
  type MutableRefObject,
  type RefObject,
} from "react";
import { Task, ViewMode } from "@thepocman/gantt-task-react";
import type { TimelineUtils } from "./timelineUtils";

type UseTimelineBarStylesArgs = {
  leftHeaderRef: RefObject<HTMLDivElement | null>;
  leftScrollRef: RefObject<HTMLDivElement | null>;
  rightPaneRef: RefObject<HTMLDivElement | null>;
  syncLockRef: MutableRefObject<"left" | "right" | null>;
  utils: TimelineUtils;
  tasks: Task[];
  viewMode: ViewMode;
};

export function useTimelineBarStyles({
  leftHeaderRef,
  leftScrollRef,
  rightPaneRef,
  syncLockRef,
  utils,
  tasks,
  viewMode,
}: UseTimelineBarStylesArgs) {
  const applyBorders = useCallback(() => {
    if (!rightPaneRef.current) return;

    const barGroups = rightPaneRef.current?.querySelectorAll<SVGGElement>(
      'g[data-testid^="task-bar-"]',
    );

    if (!barGroups || barGroups.length === 0) return;

    barGroups.forEach((group) => {
      const backgroundRect = group.querySelector<SVGRectElement>(
        'rect[class*="_barBackground_"]',
      );
      const progressRect = group.querySelector<SVGRectElement>(
        'rect[class*="_barProgress_"]',
      );

      [backgroundRect, progressRect].forEach((rect) => {
        if (!rect) return;

        const fillColor = rect.getAttribute("fill") ?? "";
        const borderColor = utils.getLegendBorderColorFromFill(fillColor);
        if (!borderColor) return;

        rect.setAttribute("stroke", borderColor);
        rect.setAttribute("stroke-width", "2");
      });
    });
  }, [rightPaneRef, utils]);

  useEffect(() => {
    const timeoutId = setTimeout(applyBorders, 100);
    return () => clearTimeout(timeoutId);
  }, [applyBorders, tasks, viewMode]);

  useEffect(() => {
    const leftScroll = leftScrollRef.current;
    const rightScroll = rightPaneRef.current?.querySelector<HTMLDivElement>(
      '[class*="_ganttTaskContent_"]',
    );

    if (!leftScroll || !rightScroll) return;

    let borderReapplyTimeout: number | null = null;

    const scheduleBorderReapply = () => {
      if (borderReapplyTimeout !== null) {
        cancelAnimationFrame(borderReapplyTimeout);
      }
      borderReapplyTimeout = requestAnimationFrame(() => {
        applyBorders();
        borderReapplyTimeout = null;
      });
    };

    const syncHeaderScroll = () => {
      if (!leftHeaderRef.current || !leftScroll) return;
      leftHeaderRef.current.scrollLeft = leftScroll.scrollLeft;
    };

    const syncFromRight = () => {
      if (syncLockRef.current === "left") return;
      if (!leftScroll || !rightScroll) return;

      if (Math.abs(leftScroll.scrollTop - rightScroll.scrollTop) < 1) return;

      syncLockRef.current = "right";
      leftScroll.scrollTop = rightScroll.scrollTop;
      syncLockRef.current = null;
      scheduleBorderReapply();
    };

    const syncFromLeft = () => {
      if (syncLockRef.current === "right") return;
      if (!leftScroll || !rightScroll) return;

      const verticalDiff = Math.abs(
        rightScroll.scrollTop - leftScroll.scrollTop,
      );
      const horizontalDiff = Math.abs(
        leftScroll.scrollLeft - (leftHeaderRef.current?.scrollLeft || 0),
      );

      if (verticalDiff >= 1) {
        syncLockRef.current = "left";
        rightScroll.scrollTop = leftScroll.scrollTop;
        syncLockRef.current = null;
        scheduleBorderReapply();
      }

      if (leftHeaderRef.current && horizontalDiff >= 1) {
        syncHeaderScroll();
      }
    };

    rightScroll.scrollTop = leftScroll.scrollTop;
    if (leftHeaderRef.current) {
      syncHeaderScroll();
    }
    rightScroll.addEventListener("scroll", syncFromRight, { passive: true });
    leftScroll.addEventListener("scroll", syncFromLeft, { passive: true });

    return () => {
      if (borderReapplyTimeout !== null) {
        cancelAnimationFrame(borderReapplyTimeout);
      }
      rightScroll.removeEventListener("scroll", syncFromRight);
      leftScroll.removeEventListener("scroll", syncFromLeft);
    };
  }, [
    leftHeaderRef,
    leftScrollRef,
    rightPaneRef,
    syncLockRef,
    tasks,
    viewMode,
    applyBorders,
  ]);
}
